import { Box, Card, Flex, Group, Stack, useMantineTheme } from '@mantine/core';
import type { MonitorTimeSeries } from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import { SelectorTarget, SelectType } from '@repo/common/components';
import { Selector } from '@repo/common/components/Selectors/Selector';
import { Text, Title } from '@repo/foundations';
import { useCallback, useMemo } from 'react';
import { useAuthUser } from '../../../../api';
import type { DateRange } from '../../../../components/DateRangeButtonGroup/DateRangeButtonGroup';
import DateRangeButtonGroup from '../../../../components/DateRangeButtonGroup/DateRangeButtonGroup';
import { dateFilterOptions } from '../../constants';
import type { ChartDataPoint, TimeRangeValue } from '../../types';
import { MetricsOverviewStackedLineChart } from './MetricsOverviewStackedLineChart';
import { processTimeseriesData } from './utils';

export function MonitorsOverviewChart({
	timeseries,
	dateFilter,
	setDateFilter,
	setTimeRangeValue,
	timeRangeValue,
	dateRangeOption,
	setDateRangeOption,
}: {
	timeseries: MonitorTimeSeries;
	dateFilter: string;
	setDateFilter: (dateFilter: string) => void;
	setTimeRangeValue: (timeRangeValue: TimeRangeValue) => void;
	timeRangeValue: TimeRangeValue;
	dateRangeOption: string;
	setDateRangeOption: (dateRangeOption: string) => void;
}) {
	const { user, workspace } = useAuthUser();
	const theme = useMantineTheme();

	const chartData = useMemo(() => {
		if (!timeseries?.data) return [];

		const sortedData = processTimeseriesData(timeseries.data);

		const mappedData: ChartDataPoint[] = sortedData.map((point) => {
			const data: ChartDataPoint = {
				date: point.timestamp,
				Normal: point.status_counts.normal,
				Error: point.status_counts.error,
				'Open incidents': point.incident_counts.active,
				'Acknowledged incidents': point.incident_counts.acknowledged,
			};

			return data;
		});

		return mappedData;
	}, [timeseries]);

	const channels = useMemo(
		() => [
			{
				label: 'Normal',
				x: 'date' as keyof (typeof chartData)[0],
				y: 'Normal' as keyof (typeof chartData)[0],
				renderArea: true,
				areaOpacity: 0.3,
				color: theme.other.getColor('icon/success/default'),
				zIndex: 10,
			},
			{
				label: 'Failed',
				x: 'date' as keyof (typeof chartData)[0],
				y: 'Error' as keyof (typeof chartData)[0],
				renderArea: true,
				areaOpacity: 0.3,
				color: theme.other.getColor('icon/critical/default'),
				zIndex: 20,
			},
			{
				label: 'Open incidents',
				x: 'date' as keyof (typeof chartData)[0],
				y: 'Open incidents' as keyof (typeof chartData)[0],
				renderArea: true,
				areaOpacity: 0.3,
				color: theme.other.getColor('icon/warning/default'),
				zIndex: 30,
			},
			{
				label: 'Acknowledged incidents',
				x: 'date' as keyof (typeof chartData)[0],
				y: 'Acknowledged incidents' as keyof (typeof chartData)[0],
				renderArea: true,
				areaOpacity: 0.3,
				color: theme.other.getColor('icon/caution/default'),
				zIndex: 40,
			},
		],
		[theme]
	);

	const tooltipDataRenderer = useCallback(
		(dataIndex: number) => {
			const point = chartData[dataIndex];
			if (!point) return null;

			const isRange = dateRangeOption === 'week' || dateRangeOption === 'month';
			const nextPoint = chartData[dataIndex + 1];
			const showRange = isRange && nextPoint;

			return (
				<Box p="sm">
					<Text mb="sm" size="sm" color="text/secondary/default">
						{showRange ? (
							<>
								{point.date.toLocaleDateString([], {
									month: 'long',
									day: 'numeric',
								})}
								{' - '}
								{nextPoint.date.toLocaleDateString([], {
									month: 'long',
									day: 'numeric',
									year: 'numeric',
								})}
							</>
						) : (
							point.date.toLocaleDateString([], {
								month: 'long',
								day: 'numeric',
								year: 'numeric',
							})
						)}
					</Text>
					<Flex direction="column" gap="xs" mt="sm">
						<Flex justify="space-between" gap="sm">
							<Flex gap="xs" align="center">
								<Box
									w={16}
									h={2}
									bg={channels[0].color}
									style={{
										borderRadius: theme.radius.md,
									}}
									aria-hidden="true"
								/>
								<Text size="sm">Normal </Text>
							</Flex>
							<Text fw={500} size="sm">
								{point.Normal}
							</Text>
						</Flex>
						<Flex justify="space-between" gap="sm">
							<Flex gap="xs" align="center">
								<Box
									w={16}
									h={2}
									bg={channels[1].color}
									style={{
										borderRadius: theme.radius.md,
									}}
									aria-hidden="true"
								/>
								<Text size="sm">Failed </Text>
							</Flex>
							<Text fw={500} size="sm">
								{point.Error}
							</Text>
						</Flex>
						<Flex justify="space-between" gap="sm">
							<Flex gap="xs" align="center">
								<Box
									w={16}
									h={2}
									bg={channels[2].color}
									style={{
										borderRadius: theme.radius.md,
									}}
									aria-hidden="true"
								/>
								<Text size="sm">Open incidents </Text>
							</Flex>
							<Text fw={500} size="sm">
								{point['Open incidents']}
							</Text>
						</Flex>
						<Flex justify="space-between" gap="sm">
							<Flex gap="xs" align="center">
								<Box
									w={16}
									h={2}
									bg={channels[3].color}
									style={{
										borderRadius: theme.radius.md,
									}}
									aria-hidden="true"
								/>
								<Text size="sm">Acknowledged incidents </Text>
							</Flex>
							<Text fw={500} size="sm">
								{point['Acknowledged incidents']}
							</Text>
						</Flex>
					</Flex>
				</Box>
			);
		},
		[chartData, dateRangeOption]
	);

	const handleDateFilterChange = useCallback(
		(values: string[]) => {
			const newDateFilter = values[0] as string;
			setDateFilter(newDateFilter);
			setTimeRangeValue({
				...timeRangeValue,
				timeRange: newDateFilter,
			});
			trackEvent(
				'monitors/overview/date_filter_change',
				{
					date_filter: newDateFilter,
				},
				user,
				workspace!
			);
		},
		[setDateFilter, setTimeRangeValue, timeRangeValue, user, workspace]
	);

	const handleDateRangeChange = useCallback(
		(range: DateRange, optionId: string) => {
			setTimeRangeValue({
				timeRange: dateFilter,
				dateRange: range,
			});
			setDateRangeOption(optionId);
			trackEvent(
				'monitors/overview/date_range_change',
				{
					date_range_option: optionId,
					date_range_from: range.from?.toISOString() ?? '',
					date_range_to: range.to?.toISOString() ?? '',
				},
				user,
				workspace!
			);
		},
		[dateFilter, setTimeRangeValue, setDateRangeOption, user, workspace]
	);

	return (
		<Card withBorder style={{ borderRadius: theme.radius.md }}>
			<Stack spacing="md" pb="xl">
				<Group position="apart">
					<Title size="sm" weight="bold">
						Monitors over time
					</Title>
					<Group position="apart" spacing="sm">
						<Selector
							initialValue={[dateFilter]}
							getItems={async () => dateFilterOptions}
							onChange={handleDateFilterChange}
							selectType={SelectType.Single}
							disableSearch
						>
							<SelectorTarget size="md" variant="default">
								{
									dateFilterOptions.find((item) => item.value === dateFilter)
										?.label
								}
							</SelectorTarget>
						</Selector>
						<DateRangeButtonGroup
							selectedOptionId={dateRangeOption}
							onChange={handleDateRangeChange}
						/>
					</Group>
				</Group>
				<MetricsOverviewStackedLineChart
					channels={channels}
					chartData={chartData}
					dateFilter={dateFilter}
					tooltipDataRenderer={tooltipDataRenderer}
					dateRangeOption={dateRangeOption}
				/>
			</Stack>
		</Card>
	);
}
